import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Icon } from "@trussworks/react-uswds";
import classNames from "classnames";
import { useEffect, useState } from "react";

// components
import AppliedFilterSentence from "components/AppliedFilterSentence";
import { Loader } from "components/icons";

// query
import useResultQueryV2 from "pages/AdvancedSearchV2/queries";

// styles
import styles from "./MarketInsights.module.scss";

// utils
import {
  returnSBACertsInsights,
  returnSelfCertsInsights,
  formatUpdateAtDate,
  formatTimestamp,
} from "utils/helper";

// constants
import {
  ActiveSBACertifications,
  FilterKeys,
  OperatorType,
  SelfCertificateMapped,
  SelfCertifications,
} from "constants/filters";

// store
import useFiltersStore from "store/useFiltersStoreNew";

// types
import { BusinessItem } from "types/results-panel.types";

export default function MarketInsights() {
  const { setFilter, filters } = useFiltersStore((state) => ({
    setFilter: state.setFilter,
    filters: state.filters,
  }));

  const navigate = useNavigate();

  const { data, isLoading, isError, dataUpdatedAt } = useResultQueryV2();

  const results = data?.results ? data.results : [];

  const showInsights = !isLoading && results && results.length > 0 && !isError;
  const sbaCertInsights = getSBAInsights(results);
  const sbaSelfCertInsights = getSelfCertInsights(results);

  const tableClasses = classNames("usa-table", styles["sba-table"]);


  return (
    <>
      <Helmet>
        <title>Market Insights | Small Business Search</title>
      </Helmet>

      <div className={styles["market-insights-root"]}>
        <Link to="/advanced" className={styles["back-link"]}>
          <Icon.ArrowBack size={3} aria-label="back button"/>
          <span>Back to results</span>
        </Link>

        <div className={styles["market-insights-content"]}>
          <h1 className={styles["title"]}>Market Insights</h1>

          {showInsights && (
            <>
              <div className={styles["sentence-group"]}>
                <p>Small business</p> <AppliedFilterSentence />
              </div>

              <p className={styles["time-stamp"]}>
                {formatTimestamp(dataUpdatedAt)}
              </p>

              <hr className={styles["divider"]} />

              {/* SBA Certification table */}
              <h2 className={styles["sub-title"]}>SBA certifications</h2>

              <table className={tableClasses}>
                <thead>
                  <tr>
                    <th scope="col">Certification type</th>
                    <th scope="col">Number of small businesses</th>
                  </tr>
                </thead>
                <tbody>
                  {sbaCertInsights &&
                    sbaCertInsights.map((cert, index) => (
                      <tr key={index}>
                        <td scope="row" style={{ verticalAlign: "text-top" }}>
                          {cert.label}
                        </td>
                        <td>
                          <div className={styles["group"]}>
                            <span>{cert.count}</span>
                            {cert.count > 0 && (
                              <button onClick={() => {
                               
                                // Sub filter is been redundent here in Sprint 36, we can directly set the filter
                                // setSubFilter(FilterKeys.SBA_CERTIFICATIONS, cert.filter);

                                setFilter(FilterKeys.SBA_CERTIFICATIONS, {
                                  ...filters.sbaCertifications,
                                  activeCerts: [...filters.sbaCertifications.activeCerts, cert.filter],
                                  operatorType: OperatorType.AND,
                                });

                                navigate("/advanced");
                              }}>
                                View list
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Self Certification table */}
              <h2 style={{ marginTop: 8 }} className={styles["sub-title"]}>
                Business type and self-certifications
              </h2>

              <p className={styles["desc"]}>
                Small businesses attested to these ownership traits when
                registering with SAM.gov. These attestations are not vetted by
                SBA.
              </p>

              <table className={tableClasses}>
                <thead>
                  <tr>
                    <th scope="col">Economic group</th>
                    <th scope="col">Number of small businesses</th>
                  </tr>
                </thead>
                <tbody>

                  {sbaSelfCertInsights &&
                    sbaSelfCertInsights.map((cert, index) => (
                      <tr key={index}>
                        <td scope="row" style={{ verticalAlign: "text-top" }}>
                          {cert.label}
                        </td>
                        <td>
                          <div className={styles["group"]}>
                            <span>{cert.count}</span>
                            {cert.count > 0 && (
                              <button onClick={() => {
                                // Sub filter is been redundent here in Sprint 36, we can directly set the filter
                                // setSubFilter(FilterKeys.SELF_CERTIFICATIONS, {
                                //   label: cert.label,
                                //   value: cert.prop,
                                // });

                                setFilter(FilterKeys.SELF_CERTIFICATIONS, {
                                  ...filters.selfCertifications,
                                  certifications: [
                                    ...filters.selfCertifications
                                      .certifications,
                                    cert.filter,
                                  ],
                                  operatorType: OperatorType.AND,
                                });

                                navigate("/advanced");
                              }}>
                                View list
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )} 

          {!isLoading && !showInsights && (
            <div className={styles["no-result"]}>
              <p>No insights found</p>
              <Link to="/advanced">Try changing your filters</Link>
            </div>
          )}

          {isLoading && (
            <div className={styles["loader"]}>
              <Loader />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function getSBAInsights(data: BusinessItem[]) {
  if (data.length === 0) return [];

  const certs = [
    {
      label: "8(a) or 8(a) Joint Venture",
      props: ["active_8a_boolean", "active_8a_jv_boolean"]
    },
    {
      label: "HUBZone",
      props: ["active_hz_boolean"]
    },
    {
      label: "Women-Owned Small Business (WOSB)",
      props: ["active_wosb_boolean"]
    },
    {
      label: "Economically-Disadvantaged Women-Owned Small Business (EDWOSB)",
      props: ["active_edwosb_boolean"]
    },
    {
      label: "Veteran-Owned Small Business (VOSB)",
      props: ["active_vosb_boolean", "active_vosb_jv_boolean"]
    },
    {
      label: "Service-Disabled Veteran-Owned Small Business (SDVOSB)",
      props: ["active_sdvosb_boolean", "active_sdvosb_jv_boolean"]
    }
  ];

  const fullCerts = certs.map((cert) => {
    return {
      ...cert,
      count: data.filter((item) => 
      cert.props.some((prop) => item[prop] === true)
      ).length,
      filter: ActiveSBACertifications.find((item) => item.label === cert.label)
    }
  })

  return fullCerts;

}

function getSelfCertInsights(data: BusinessItem[]) {
  if (data.length === 0) return [];

  const certs = [
    {
      label: "Self-Certified Small Disadvantaged Business",
      samLabel: "Self-Certified Small Disadvantaged Business",
      prop: "self_small_boolean"
    },
    {
      label: "Self-Certified Veteran-Owned Small Business",
      samLabel: "Veteran-Owned Business",
      prop: "self_vosb_boolean"
    },
    {
      label: "Self-Certified Woman-Owned Small Business",
      samLabel: "Women-Owned Small Business",
      prop: "self_wosb_boolean"
    },
    {
      label: "Self-Certified Woman-Owned Small Business Joint Venture",
      samLabel: "Women-Owned Small Business Joint Venture",
      prop: "self_wosb_jv_boolean"
    },
    {
      label: "Self-Certified HUBZone Joint Venture",
      samLabel: "Self-Certified HUBZone Joint Venture",
      prop: "self_hubzone_jv_boolean"
    },
    {
      label: "Community Development Corporation (CDC) Owned Small Business",
      samLabel: "Community Development Corporation Owned Firm",
      prop: "self_cdc_boolean"
    },
    {
      label: "Native American Owned",
      samLabel: "Native American Owned",
      prop: "self_native_owned_boolean"
    },
    {
      label: "Tribally Owned Small Business",
      samLabel: "Tribally Owned Firm",
      prop: "self_tribal_owned_boolean"
    },
    {
      label: "American Indian Owned Small Business",
      samLabel: "American Indian Owned",
      prop: "self_american_indian_owned_boolean"
    },
    {
      label: "Alaskan Native Corp (ANC) Owned Small Business",
      samLabel: "Alaskan Native Corporation Firm",
      prop: "self_anc_owned_boolean"
    },
    {
      label: "Native Hawaiian Org (NHO) Owned Small Business",
      samLabel: "Native Hawaiian Organization Owned Firm",
      prop: "self_nho_boolean"
    }
    
    // missing snowflake field
    // {
    //   label: "Self-Certified HUBZone Joint Venture",
    //   prop: "self_tribal_owned_boolean"
    // },
  ];

  const fullSelfCerts = certs.map((cert) => {
    return {
      ...cert,
      count: data.filter((item) => item.meili_self_certifications.some(x => x === cert.samLabel)).length,
      filter: SelfCertifications.find((item) => item.label === cert.label)
    }
  })

  return fullSelfCerts;
}

function refactorData(data) {
  return data.map((result) => ({
    ...result,
    pronetID: result.pronet_id,
    businessName: result.name,
    business_trade_name: result.business_trade_name,
    capabilitiesNarrative: result.capabilities,
    capabilitiesStatement: result.capabilities_link,
    active_certifications: result.active_certifications || [],
    contact: result.contact,
    email: result.email,
    address: result.address,
    ein: result.ein,
    uei: result.uei,
    last_updated: result.last_updated,
  }))
}